// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ethers } from 'ethers'
import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { StageConfiguration } from './DeploymentConfigTypes'
import { RoleEnum, StageName, yupErc20WeiRequired, yupEthAddrOptional, yupTxHashOptional } from './Enums'
import { LiquidityAssetCTO, LiquidityAssetSchema } from './LiquidityAssetCTO'
import { LytReservesCTO, LytReservesSchema } from './LytReservesCTO'
import { PoolDynamicOverviewCTO } from './PoolDynamicCTO'
import { DynamicWithdrawalActivityCTO } from './PoolEventCTO'
import { PoolLytOverviewCTO } from './PoolLytOverviewCTO'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export enum PlanActionEnum {
  UNDEFINED = 'UNDEFINED',
  //  MintMarketMakerNonNative = 'Mint for Non-native Market Maker Mint',
  ReserveNativeDeposit = 'ReserveNative Deposit',
  ReserveBufferRetainDeposit = 'Reserve Buffer Retain Deposit',
  ReserveRedeemBufferRetain = 'Reserve Redeem Buffer Retain',
  //  ReserverMintWithNonNative = 'Mint for Non-native Reserve Transfer',
  // TransferNonNativeToReserves = 'Non-Native Transfer To Reserves',
  TransferNativeForMarketMakerRedeem = 'Transfer to LytPool for Market Maker Redeem',
  ProcessRedemptionForMarketMaker = 'Process Market Maker Redemption',
  RequestReserveRedeem = 'Request Reserve Redeem',
}

const PlanActionSchemaValuesForCasting = {
  id: yup
    .string()
    .default(ethers.uuidV4(ethers.randomBytes(16)))
    .label('PoolEventId ')
    .required()
    .meta(toFieldMetaData({ propName: 'id', description: 'ID of event ', stringType: StringTypeEnum.ID })),
  stageName: yup
    .string<StageName>()
    .label('stageName')
    .required()
    .meta(toFieldMetaData({ propName: 'stageName', description: 'stageName' })),

  chainId: yup
    .number()
    .label('chainId')
    .required()
    .meta(toFieldMetaData({ propName: 'chainId', description: 'chainConfigurationName' })),

  roleEnum: yup
    .mixed<RoleEnum>()
    .oneOf(Object.values(RoleEnum) as unknown as RoleEnum[])
    .defined()
    .label('Role')
    .meta(toFieldMetaData({ propName: 'roleEnum', description: 'Role of the action' })),

  actionEnum: yup
    .mixed<PlanActionEnum>()
    .oneOf(Object.values(PlanActionEnum) as unknown as PlanActionEnum[])
    .defined()
    .label('Action')
    .meta(toFieldMetaData({ propName: 'action', description: 'Action of the assigned action' })),
  assets: yupErc20WeiRequired
    .label('Assets')
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'assets',
        description: 'The assets for the action',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  fees: yupErc20WeiRequired
    .label('Fees')
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'fees',
        description: 'The Fees for the action',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  shares: yupErc20WeiRequired
    .label('Shares')
    .default('0')
    .meta(
      toFieldMetaData({
        propName: 'shares',

        description: 'the shares for the action',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  lytPoolAddr: yupEthAddrOptional.label('lytPoolAddr').meta(
    toFieldMetaData({
      propName: 'lytPoolAddr',
      description: 'lytPoolAddr',
    })
  ),
  lytVaultAddr: yupEthAddrOptional.label('lytVaultAddr').meta(
    toFieldMetaData({
      propName: 'lytVaultAddr',
      description: 'lytVaultAddr',
    })
  ),
  marketMakerAddr: yupEthAddrOptional.label('marketMakerAddr').meta(
    toFieldMetaData({
      propName: 'marketMakerAddr',
      description: 'marketMakerAddr',
    })
  ),
  txHash: yupTxHashOptional.label('txHash').meta(
    toFieldMetaData({
      propName: 'txHash',
      description: 'txHash',
    })
  ),
  uuid: yup
    .string()
    .uuid()
    .optional()
    .label('Event Id')
    .meta(toFieldMetaData({ propName: 'uuid', stringType: StringTypeEnum.ID, description: 'UUID of Redemptions' })),
}
export const PlanActionSchema = yup.object({
  ...PlanActionSchemaValuesForCasting,
  reserve: LytReservesSchema.label('reserve')
    .optional()
    .meta(
      toFieldMetaData({
        propName: 'reserve',
        description: 'reserve',
      })
    ),
  liquidityAsset: LiquidityAssetSchema.label('liquidityAsset')
    .optional()
    .meta(
      toFieldMetaData({
        propName: 'liquidityAsset',
        description: 'liquidityAsset',
      })
    ),
})

export const PlanActionSchemaForCasting = yup.object(PlanActionSchemaValuesForCasting)

export interface PlanActionCTO {
  id: string
  stageName: StageName
  chainId: number
  roleEnum: RoleEnum
  actionEnum: PlanActionEnum
  reserve?: LytReservesCTO
  liquidityAsset?: LiquidityAssetCTO
  assets: ERC20Wei
  shares: ERC20Wei
  fees: ERC20Wei
  lytPoolAddr?: EthAddr
  lytVaultAddr?: EthAddr
  marketMakerAddr?: EthAddr
  txHash?: string
  uuid?: string
}

type PlanActionCTOInfer = InferType<typeof PlanActionSchema>

export const _testALytEvent: PlanActionCTO = {} as unknown as PlanActionCTOInfer
export const _testBLytEvent: PlanActionCTOInfer = {} as unknown as PlanActionCTO

export interface ReservePlanParams {
  lytPoolAddr: EthAddr
  lytStageConfiguration: StageConfiguration
  otStageConfiguration: StageConfiguration
}

export interface LiquidityAssetWithBalanceCTO extends LiquidityAssetCTO {
  lytIssuerVaultBalance: ERC20Wei
  lytPoolBalance: ERC20Wei
  lytOutstandingRedeemBalance: ERC20Wei
  lytOutstandingRequestedBalance: ERC20Wei
  lytOutstandingAcceptedBalance: ERC20Wei
  reserveTransferBalance: ERC20Wei
  lytIssuerVaultBalanceAfterActions: ERC20Wei
  lytPoolBalanceAfterActions: ERC20Wei
  reserveTransferBalanceAfterActions: ERC20Wei
  reserveOutstandingWithdrawals: ERC20Wei
}
export interface LytReservesCTOWithDataCTO extends LytReservesCTO {
  withdrawActivityCTOs: DynamicWithdrawalActivityCTO[]
  percentReservesBeforeActions: number
  percentReservesAfterActions: number
  minBalanceAmount: ERC20Wei
  maxBalanceAmount: ERC20Wei
  depositNeededAmount: ERC20Wei
  withdrawRequestNeededAmount: ERC20Wei
  withdrawTotalNeededAmount: ERC20Wei
  nativeDepositAmount: ERC20Wei
  //offChainDepositAmount: ERC20Wei
  withdrawRequestedOrAcceptedAmount: ERC20Wei
}
export interface LytReservesCTOAllocationCTO {
  lytPoolAddr: EthAddr
  timestamp: number
  reserves: LytReservesCTOWithDataCTO[]
}
export interface ReserveActionPlanCTO {
  lytPoolAddr: EthAddr
  lytPoolOverview: PoolLytOverviewCTO
  liquidityAssets: LiquidityAssetWithBalanceCTO[]
  nativeLiquidityAsset: LiquidityAssetWithBalanceCTO
  lytIssuerVaultAddr: EthAddr

  lytWithdrawActivityCTOs: DynamicWithdrawalActivityCTO[]
  possibleReservePools: PoolDynamicOverviewCTO[]
  initialReserveAllocation: LytReservesCTOAllocationCTO
  planActions: PlanActionCTO[]
  reserveAllocationsWithActions: LytReservesCTOWithDataCTO[]
}
